import { lang } from '@umu/ukit-lang';
import { Modal, SnackBar } from '@umu/umu-design';
import copy from 'copy-to-clipboard';
import { IShareInfo, IShareIconItem } from '../interface/share';
import { openWindow } from '@umu/umu-dom';
import { setCertificatePermission } from '../api/server';
import { ICertificateConfigInfo } from '../interface/caetConfig';
import linkedinIcon from '../../../images/icon-linkedin.svg';
import whatsappIcon from '../../../images/icon-whatsapp.svg';
import facebookIcon from '../../../images/icon-facebook.svg';
import twitterIcon from '../../../images/icon-twitter.svg';
import emailIcon from '../../../images/icon-email.svg';
const { confirm } = Modal;
export const MAX_USER_NAME_LENGTH = 20;

export const defaultConfig: ICertificateConfigInfo = {
    userName: '',
    title: '',
    subTitle: '',
    qr: '',
    link: '',
    theme: '',
    date: '',
    certificateId: '',
};

export const handleDefaultAfterReceive = () => {
    confirm({
        title: lang('稍后领取'),
        centered: true,
        content: lang('稍后可以在课程详情页或在课程市场页的AI力分类旁点击进入“CAET证书”页面领取。'),
        okText: lang('确认'),
        cancelText: '',
    });
};
/**
 * 分享图标滚动阈值
 * 当图标数量超过此值时，启用滚动模式
 */
export const SHARE_ICON_SCROLL_THRESHOLD = 4;
/**
 * 管理证书链接 ${id} 为证书链接
 */
export const MANAGE_CERTIFICATE_LINK = '/caet/${id}';
export const jumpToManageCertificate = (id: string, target: '_blank' | '_self' = '_blank') => {
    openWindow(MANAGE_CERTIFICATE_LINK.replace('${id}', id), target);
};

/**
 * 生成 LinkedIn 分享链接
 * @param url 要分享的 URL
 * @param params LinkedIn 分享参数
 * @returns LinkedIn 分享链接
 */
export const generateLinkedInShareUrl = (
    url: string,
    params?: {
        title?: string;
        summary?: string;
        source?: string;
    },
): string => {
    if (!url) {
        return '';
    }

    // 基础 URL 使用新的 LinkedIn 分享 API
    const baseUrl = 'https://www.linkedin.com/sharing/share-offsite/';
    const urlParams = new URLSearchParams();

    // 添加 URL 参数
    urlParams.append('url', url);

    // 添加可选参数
    if (params?.title) {
        urlParams.append('title', params.title);
    }
    if (params?.summary) {
        urlParams.append('summary', params.summary);
    }
    if (params?.source) {
        urlParams.append('source', params.source);
    }
    return `${baseUrl}?${urlParams.toString()}`;
};
/**
 * 生成分享链接的参数接口
 */
export interface IGenerateShareLinkParams {
    /** 原始链接 */
    originalLink: string;
    /** 是否多选模式 */
    multiple: boolean;
    /** 选中的图标key列表 */
    selectedIcons: string[];
    /** 图标列表 */
    iconList: IShareIconItem[];
    /** LinkedIn 分享额外参数 */
    linkedinParams?: {
        title?: string;
        summary?: string;
        source?: string;
    };
}

/**
 * 生成分享链接
 * 根据选中的图标类型和链接生成对应的分享链接
 */
export const generateShareLink = (params: IGenerateShareLinkParams): string => {
    const { originalLink, multiple, selectedIcons, iconList, linkedinParams } = params;

    // 如果没有选中任何图标，返回原始链接
    if (selectedIcons.length === 0) {
        return originalLink;
    }

    // 单选模式：只使用第一个选中的图标
    const iconsToUse = multiple ? selectedIcons : [selectedIcons[0]];

    // 根据图标类型生成不同的分享链接
    const shareLinks = iconsToUse.map((iconKey) => {
        const iconItem = iconList.find((icon) => icon.key === iconKey);
        if (!iconItem) {
            return originalLink;
        }
        const encodedLink = encodeURIComponent(originalLink);
        // 根据不同的分享平台生成对应的分享链接
        switch (iconKey) {
            case 'linkedin':
                // 使用新的 LinkedIn 分享 API 格式，支持额外参数
                return generateLinkedInShareUrl(originalLink, linkedinParams);
            case 'email':
                return `mailto:?subject=${encodeURIComponent('分享链接')}&body=${encodedLink}`;
            case 'whatsapp':
                return `https://wa.me/?text=${encodedLink}`;
            case 'facebook':
                return `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`;
            case 'twitter':
                return `https://twitter.com/intent/tweet?url=${encodedLink}`;
            default:
                return originalLink;
        }
    });

    // 多选模式：返回所有链接，用换行分隔
    // 单选模式：返回单个链接
    return multiple ? shareLinks.join('\n') : shareLinks[0];
};

/**
 * @description: 获取权限标签
 * @param {boolean} isPublic
 * @returns {string}
 */
const getPermissionLabel = (isPublic: boolean): string => {
    return isPublic ? lang('公开') : lang('私密设置');
};

/**
 * @description: 复制链接到剪贴板
 * @param {string} link - 要复制的链接
 * @returns {Promise<void>}
 */

const copyLinkToClipboard = (link: string): void => {
    try {
        if (!link) {
            return;
        }

        const success = copy(link);
        if (success) {
            SnackBar.show({
                message: lang('复制成功'),
            });
        } else {
            SnackBar.show({
                message: lang('复制失败'),
            });
        }
    } catch (error) {
        console.error('Copy failed:', error);
        SnackBar.show({
            message: lang('复制失败'),
        });
    }
};
/**
 * @description: 依据系统获取分享图标列表
 */
const getShareIconList = (): IShareIconItem[] => {
    return [
        { key: 'linkedin', icon: 'linkedin', src: linkedinIcon, label: lang('领英') },
        { key: 'email', icon: 'email', src: emailIcon, label: lang('电子邮件') },
        { key: 'whatsapp', icon: 'whatsapp', src: whatsappIcon, label: lang('WhatsApp') },
        { key: 'facebook', icon: 'facebook', src: facebookIcon, label: lang('Facebook') },
        { key: 'twitter', icon: 'twitter', src: twitterIcon, label: lang('X') },
    ];
};
/**
 * @description: 默认分享成功回调
 * @param {IShareInfo} shareInfo
 * @param {string} id
 * @returns {ISuccess: boolean; message?: string}
 */
const defaultCaetShareConfirm = (
    shareInfo: IShareInfo,
    id: string,
): Promise<{ isSuccess: boolean; message?: string }> => {
    return setCertificatePermission({
        id,
        permission: shareInfo.permission,
    });
};
export { getShareIconList, defaultCaetShareConfirm, getPermissionLabel, copyLinkToClipboard };
